import React from "react";
import { Formik, Form, ErrorMessage } from "formik";
import * as Yup from "yup";

import { useAppDispatch, useAppSelector } from "@/redux/store";
import { fetchSendPhone } from "@/redux/profile/settings/changePass/asyncActions";
import { addPhone } from "@/redux/profile/settings/changePass/slice";

import { Button, PhoneInput } from "@/components";

import styles from "./PhoneForm.module.scss";

import { ValidMessages } from "@/resources/locale";
import { SubmitValueType } from "./types";
import { cleanPhone } from "@/utils/form/phoneMask";

type PhoneFormProps = {
  handleSubmitForm?: () => void
}
const SMS_COOLDOWN_KEY = 'sms_last_sent';
const SMS_COOLDOWN_MS = 60000;

function getSmsCooldownRemaining(): number {
  const lastSent = sessionStorage.getItem(SMS_COOLDOWN_KEY);
  if (!lastSent) return 0;
  const elapsed = Date.now() - Number(lastSent);
  return elapsed < SMS_COOLDOWN_MS ? Math.ceil((SMS_COOLDOWN_MS - elapsed) / 1000) : 0;
}

export const PhoneForm: React.FC<PhoneFormProps> = (props) => {
  const { handleSubmitForm } = props;

  const dispatch = useAppDispatch();
  const { isLoading, errorMessage } = useAppSelector(state => state.changePass);
  const [cooldown, setCooldown] = React.useState(getSmsCooldownRemaining());

  React.useEffect(() => {
    if (cooldown <= 0) return;
    const interval = setInterval(() => {
      const remaining = getSmsCooldownRemaining();
      setCooldown(remaining);
      if (remaining <= 0) clearInterval(interval);
    }, 1000);
    return () => clearInterval(interval);
  }, [cooldown]);

  const initialValues = {
    phone: "",
  };

  const validationSchema = Yup.object({
    phone: Yup.string()
      .min(18, ValidMessages.inputs.phone.NOT_FULL)
      .required(ValidMessages.inputs.phone.REQUIRED),
  });

  const handleSubmit = async (values: SubmitValueType) => {
    if (getSmsCooldownRemaining() > 0) return;

    const phone = cleanPhone(values.phone);

    dispatch(addPhone(phone));
    const result = await dispatch(fetchSendPhone({ phone }));

    if (fetchSendPhone.fulfilled.match(result)) {
      sessionStorage.setItem(SMS_COOLDOWN_KEY, String(Date.now()));
      setCooldown(60);
      handleSubmitForm && handleSubmitForm();
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      validateOnChange={false}
      validateOnBlur={true}
    >
      {(formik) => {
        const isPhoneFilled = formik.values.phone.replace(/[^0-9]/g, "").length >= 11;

        return (
          <Form className={styles.form}>
            <div className={styles.formSection}>
              <PhoneInput name="phone" lable="Телефон" placeholder="+7 (___) ___-__-__" />
              <ErrorMessage
                component="div"
                className={styles.errorMessage}
                name="phone"
              />
            </div>
            {
              errorMessage ? (
                <div className={styles.errorMessage}>
                  {errorMessage}
                </div>
              ) : null
            }
            <Button
              type="submit"
              newStyles={styles.button}
              disabled={formik.isSubmitting || !isPhoneFilled || cooldown > 0}
            >
              { isLoading ? "Отправка..." : cooldown > 0 ? `Повторить через ${cooldown} сек` : "Восстановить" }
            </Button>
          </Form>
        );
      }}
    </Formik>
  );
};
